import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from '@prisjakt/ui-app-context';
import { useSelector } from 'react-redux';
import { ProductCollectionFilter, AggregationSection } from '../../../../types';
import { SIDEBAR_STATE } from '../../../sidebar/constants';
import FilterSection, {
  isValidSection,
  isDisabledSection,
} from './filter-section';
import * as S from './styles';

export const FILTER_PORTAL_ID = 'sidebar-filter';

type Props = {
  filters: ProductCollectionFilter[];
  sections: AggregationSection[];
};

const SidebarFilter = ({ filters, sections }: Props) => {
  const { t } = React.useContext(AppContext);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>();
  const sidebarTab = useSelector((state: any) => state.areas.sidebar.tab);

  /**
   * The whole filter slice is rendered through a portal in order to keep it
   * within the route data structure while still being able to show it in the
   * sidebar. We have to do this little dance with local state and an effect
   * to make sure the portal target has been rendered before trying to render
   * into it.
   */
  useEffect(() => {
    if (sidebarTab === SIDEBAR_STATE.FILTER) {
      setPortalTarget(document.getElementById(FILTER_PORTAL_ID));
    }
  }, [sidebarTab]);

  if (!portalTarget) {
    return null;
  }

  return ReactDOM.createPortal(
    <S.Filter>
      <S.FilterTitle>{t('filter', 'filter.title')}</S.FilterTitle>

      {sections && sections.some(isValidSection) ? (
        <S.SectionList>
          {sections
            // Don't show unsupported sections
            .filter(isValidSection)
            // Don't show unusable sections
            .filter(section => !isDisabledSection(section))
            .map(section => (
              <S.SectionListItem key={section.id}>
                <FilterSection section={section} showTitle />
              </S.SectionListItem>
            ))}
        </S.SectionList>
      ) : (
        <S.NoContent>{t('filter', 'filter.no-advanced-filters')}</S.NoContent>
      )}
    </S.Filter>,
    portalTarget
  );
};

export default SidebarFilter;
